/* Private Shopping の予約フォーム（ご来店日時・人数・ご覧になりたい作品・備考）。2026-10-02 追加。
   ・日時はカレンダーで選ぶ。選んだら注文フォームの id（バリエーション）を書き換える
   ・選んだ内容を、注文フォームにつないだ隠し欄へ写す（空の欄は送らない）
   ・「カートに入れる」の送信を先に受け取り、必須（日時・人数・作品）が空なら止めて知らせる
   ・「カートに入れる」の真上に、選んだ内容を1行でまとめて出す
   文言は Liquid（ブロックの設定）から受け取る。月や曜日の名前はページの言語で自動に出す。
   このファイルには日本語を持たせない。 */
(() => {
  'use strict';
  if (customElements.get('lounge-booking-fields')) return;

  const memory = new Map(); // product id → 選んだ内容（テーマが描き直しても戻すため）
  /* 入れた内容をこのブラウザに7日間覚えておく。カートに入れたあと内容を直したくなったとき、
     最初から入れ直さなくて済むように（本人の指示 2026-10-03）。保存できない環境では何もしない。 */
  const KEEP_MS = 7 * 24 * 60 * 60 * 1000;
  const storeKey = (id) => `lounge-booking:${id}`;
  const loadSaved = (id) => {
    try {
      const v = JSON.parse(localStorage.getItem(storeKey(id)) || 'null');
      if (v && Date.now() - (v.t || 0) < KEEP_MS) return v;
    } catch (e) { /* 使えないときは覚えない */ }
    return null;
  };
  const saveNow = (id, v) => {
    try { localStorage.setItem(storeKey(id), JSON.stringify({ ...v, t: Date.now() })); } catch (e) { /* 何もしない */ }
  };
  const lang = () => document.documentElement.lang || navigator.language || 'en';
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* '10.16 / Fri' → Date（年は今日にいちばん近い年） */
  const parseDate = (label) => {
    const m = /^(\d{1,2})\.(\d{1,2})/.exec((label || '').trim());
    if (!m) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const cands = [-1, 0, 1].map((k) => new Date(today.getFullYear() + k, +m[1] - 1, +m[2]));
    return cands.reduce((a, b) => (Math.abs(b - today) < Math.abs(a - today) ? b : a));
  };
  /* 'A (11:00~13:00)' → '11:00 – 13:00' */
  const timeText = (label) => {
    const m = /(\d{1,2}:\d{2})\s*[~〜-]\s*(\d{1,2}:\d{2})/.exec(label || '');
    return m ? `${m[1]} – ${m[2]}` : (label || '').trim();
  };
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  /* 日本の祝日（フッターのお店のカレンダーと同じ読み方。失敗しても色が付かないだけ） */
  const HOLIDAY_KEY = 'AIzaSyBWdcGSxVoZluEEnblrLUOZfxuqp_Qi7bE';   // 公開前提の識別子（loungesai.com からだけ使える）
  const HOLIDAY_CAL = 'ja.japanese#holiday@group.v.calendar.google.com';
  let holidays = null;
  const loadHolidays = (from, to) => {
    if (holidays) return holidays;
    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(HOLIDAY_CAL)}/events`
      + `?key=${HOLIDAY_KEY}&singleEvents=true&maxResults=100&timeMin=${from.toISOString()}&timeMax=${to.toISOString()}`;
    holidays = fetch(url).then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => new Set((d.items || []).map((e) => e.start && e.start.date).filter(Boolean)))
      .catch(() => new Set());
    return holidays;
  };

  class LoungeBookingFields extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.key = this.dataset.productId;
      this.error = this.querySelector('.lounge-booking__error');
      this.summary = this.querySelector('[data-summary]');
      this.loadSlots();
      this.restore();
      this.restored = true;
      this.holidays = new Set();
      this.renderCalendar();
      const [min, max] = this.monthBounds();
      loadHolidays(min, new Date(max.getFullYear(), max.getMonth() + 1, 1)).then((set) => {
        this.holidays = set;
        if (this.isConnected) this.renderCalendar();
      });
      this.addEventListener('change', this.onInput);
      this.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onInput);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('click', this.onClick);
    }

    get form() {
      return document.getElementById(this.formId);
    }

    /* ---------- 日時 ---------- */
    loadSlots() {
      let list = [];
      try { list = JSON.parse(this.querySelector('[data-slots]').textContent); } catch { list = []; }
      this.days = new Map(); // 'YYYY-MM-DD' → [{id, time, available}]
      for (const v of list) {
        const d = parseDate(v.date);
        if (!d) continue;
        const k = ymd(d);
        if (!this.days.has(k)) this.days.set(k, { date: d, slots: [] });
        this.days.get(k).slots.push({ id: String(v.id), time: timeText(v.time), raw: v.time, available: !!v.available });
      }
      for (const day of this.days.values()) day.slots.sort((a, b) => a.raw.localeCompare(b.raw));
      const firstOpen = [...this.days.values()].filter((d) => d.slots.some((x) => x.available))
        .sort((a, b) => a.date - b.date)[0];
      const base = firstOpen ? firstOpen.date : new Date();
      this.month = new Date(base.getFullYear(), base.getMonth(), 1);
      this.picked = { day: '', id: '' };
    }

    monthBounds() {
      const all = [...this.days.values()].map((d) => d.date).sort((a, b) => a - b);
      if (!all.length) return [this.month, this.month];
      const first = new Date(all[0].getFullYear(), all[0].getMonth(), 1);
      const last = all[all.length - 1];
      return [first, new Date(last.getFullYear(), last.getMonth(), 1)];
    }

    renderCalendar() {
      const grid = this.querySelector('[data-cal-grid]');
      if (!grid) return;
      const L = lang();
      const [min, max] = this.monthBounds();
      this.querySelector('[data-cal-month]').textContent =
        new Intl.DateTimeFormat(L, { year: 'numeric', month: 'long' }).format(this.month);
      this.querySelector('[data-cal-prev]').disabled = this.month <= min;
      this.querySelector('[data-cal-next]').disabled = this.month >= max;

      const wd = new Intl.DateTimeFormat(L, { weekday: 'short' });
      const cells = [];
      for (let i = 0; i < 7; i++) {               // 日曜はじまり
        const d = new Date(2026, 0, 4 + i);       // 2026-01-04 は日曜
        const tone = i === 0 ? ' is-sun' : i === 6 ? ' is-sat' : '';
        cells.push(`<span class="lounge-booking__cal-wd${tone}" role="columnheader">${wd.format(d)}</span>`);
      }
      const y = this.month.getFullYear(), m = this.month.getMonth();
      const lead = new Date(y, m, 1).getDay();
      const count = new Date(y, m + 1, 0).getDate();
      for (let i = 0; i < lead; i++) cells.push('<span class="lounge-booking__cal-day is-empty" aria-hidden="true"></span>');
      const full = new Intl.DateTimeFormat(L, { month: 'long', day: 'numeric', weekday: 'long' });
      for (let n = 1; n <= count; n++) {
        const d = new Date(y, m, n), k = ymd(d), day = this.days.get(k);
        const open = !!day && day.slots.some((x) => x.available);
        const tone = (d.getDay() === 0 || this.holidays.has(k)) ? 'is-sun' : d.getDay() === 6 ? 'is-sat' : '';
        const cls = ['lounge-booking__cal-day', open ? 'is-open' : '', tone, this.picked.day === k ? 'is-picked' : ''].join(' ');
        cells.push(open
          ? `<button type="button" class="${cls}" data-day="${k}" aria-pressed="${this.picked.day === k}" aria-label="${full.format(d)}"><span>${n}</span></button>`
          : `<span class="${cls}" aria-disabled="true">${n}</span>`);
      }
      grid.innerHTML = cells.join('');
      this.renderTimes();
    }

    renderTimes() {
      const box = this.querySelector('[data-times]');
      const day = this.days.get(this.picked.day);
      if (!day) { box.hidden = true; return; }
      box.hidden = false;
      this.querySelector('[data-times-label]').textContent =
        new Intl.DateTimeFormat(lang(), { month: 'long', day: 'numeric', weekday: 'short' }).format(day.date);
      const list = this.querySelector('[data-times-list]');
      list.innerHTML = day.slots.map((x) => `
        <label class="lounge-booking__pill${x.available ? '' : ' is-full'}${this.picked.id === x.id ? ' selected' : ''}">
          <input class="visually-hidden" type="radio" name="${this.key}-slot" value="${x.id}"
            ${this.picked.id === x.id ? 'checked' : ''} ${x.available ? '' : 'disabled'}>
          <span class="lounge-booking__pill-text">${x.time}</span>
        </label>`).join('');
    }

    pickDay(k) {
      const day = this.days.get(k);
      if (!day) return;
      this.picked.day = k;
      const open = day.slots.filter((x) => x.available);
      // 時間が1つだけの日は、それを選んだ状態にする
      this.picked.id = open.length === 1 ? open[0].id : (open.some((x) => x.id === this.picked.id) ? this.picked.id : '');
      this.renderCalendar();
      this.sync();
    }

    onClick = (event) => {
      const dialog = this.querySelector('[data-terms-dialog]');
      if (dialog && event.target.closest('[data-terms-open]')) { dialog.showModal(); return; }
      if (dialog && (event.target.closest('[data-terms-close]') || event.target === dialog)) { dialog.close(); return; }
      const day = event.target.closest('[data-day]');
      if (day) { this.pickDay(day.dataset.day); return; }
      const step = event.target.closest('[data-cal-prev], [data-cal-next]');
      if (step && !step.disabled) {
        const dir = step.hasAttribute('data-cal-next') ? 1 : -1;
        this.month = new Date(this.month.getFullYear(), this.month.getMonth() + dir, 1);
        this.renderCalendar();
      }
    };

    /* ---------- 入力の読み書き ---------- */
    onInput = (event) => {
      if (event.target.name === `${this.key}-slot`) this.picked.id = event.target.value;
      this.sync();
      if (!this.error.hidden && this.isValid()) this.showError(false);
    };

    read() {
      const guest = this.querySelector('[data-field="guests"] input:checked');
      const picks = [...this.querySelectorAll('[data-field="interest"] input:checked')];
      const note = this.querySelector('.lounge-booking__note');
      return {
        slot: this.picked.id,
        guests: guest ? guest.value : '',
        guestsLabel: guest ? guest.dataset.label : '',
        interest: picks.map((x) => x.value),
        interestLabel: picks.map((x) => x.dataset.label),
        note: note ? note.value.trim() : '',
        agree: !!this.agreed,   // 確認の窓で「上記の内容に同意して予約」に印を付けて進んだとき
        needsAgree: false,
      };
    }

    restore() {
      const saved = memory.get(this.key) || loadSaved(this.key);
      if (!saved) return;
      // 覚えていた枠がもう無い・埋まっているときは、日時だけ選び直してもらう
      const day = this.days.get(saved.day || '');
      const slotOk = !!day && day.slots.some((x) => x.id === saved.slot && x.available);
      this.picked = slotOk ? { day: saved.day, id: saved.slot } : { day: '', id: '' };
      if (this.picked.day && this.days.has(this.picked.day)) {
        const d = this.days.get(this.picked.day).date;
        this.month = new Date(d.getFullYear(), d.getMonth(), 1);
      }
      this.querySelectorAll('[data-field="guests"] input').forEach((x) => { x.checked = x.value === saved.guests; });
      this.querySelectorAll('[data-field="interest"] input').forEach((x) => { x.checked = (saved.interest || []).includes(x.value); });
      const note = this.querySelector('.lounge-booking__note');
      if (note) note.value = saved.note;
    }

    sync() {
      const v = this.read();
      const keep = { day: this.picked.day, slot: v.slot, guests: v.guests, interest: v.interest, note: v.note };
      memory.set(this.key, keep);
      if (this.restored) saveNow(this.key, keep);   // 読み込み直後の空の状態で上書きしない
      // 注文フォームのバリエーション
      const idInput = this.form && this.form.querySelector('input[name="id"], select[name="id"]');
      if (idInput && v.slot) idInput.value = v.slot;

      const sep = lang().startsWith('ja') ? '、' : ', ';
      const out = {
        guests: v.guestsLabel,
        guests_code: v.guests,
        interest: v.interestLabel.join(sep),
        interest_code: v.interest.join(','),
        note: v.note,
        note_code: v.note,
        agree: v.agree ? (this.querySelector('[data-out="agree"]')?.dataset.value || '1') : '',
        agree_code: v.agree ? '1' : '',
      };
      this.querySelectorAll('[data-out]').forEach((input) => {
        const value = out[input.dataset.out] || '';
        input.value = value;
        input.disabled = value === '';   // 空の欄は注文に乗せない
      });
      this.querySelectorAll('.lounge-booking__pill, .lounge-booking__tile, .lounge-booking__agree').forEach((label) => {
        label.classList.toggle('selected', !!label.querySelector('input:checked'));
      });
      this.renderSummary(v);
    }

    renderSummary(v) {
      if (!this.summary) return;
      const day = this.days.get(this.picked.day);
      const slot = day && day.slots.find((x) => x.id === v.slot);
      if (!slot) { this.summary.hidden = true; this.summary.textContent = ''; return; }
      const date = new Intl.DateTimeFormat(lang(), { month: 'long', day: 'numeric', weekday: 'short' }).format(day.date);
      const parts = [`${date}${lang().startsWith('ja') ? '\u3000' : ' '}${slot.time}`];
      if (v.guests) {
        const fmt = this.dataset.guestsFormat || '{n}';
        parts.push(/^\d+$/.test(v.guests) ? fmt.replace('{n}', v.guestsLabel) : v.guestsLabel);
      }
      this.summary.textContent = parts.join(' ・ ');
      this.summary.hidden = false;
    }

    isValid() {
      const v = this.read();
      return this.fieldsDone(v) && (!v.needsAgree || v.agree);
    }

    fieldsDone(v) {
      return v.slot !== '' && v.guests !== '' && v.interest.length > 0;
    }

    showError(on) {
      const v = this.read();
      // 選ぶ欄が埋まっていて、同意だけが無いときは、同意のお知らせを出す
      const msg = this.fieldsDone(v) ? (this.dataset.agreeError || this.dataset.error) : this.dataset.error;
      this.error.textContent = on ? msg : '';
      this.error.hidden = !on;
      const empty = { slot: !v.slot, guests: !v.guests, interest: !v.interest.length, agree: v.needsAgree && !v.agree };
      this.querySelectorAll('[data-field="slot"], [data-field="guests"], [data-field="interest"], [data-field="agree"]').forEach((field) => {
        field.toggleAttribute('data-invalid', on && empty[field.dataset.field]);
      });
    }

    /* ---------- 確認の窓（予約注文と同じ流れ） ---------- */
    openConfirm() {
      const dlg = this.querySelector('[data-confirm]');
      if (!dlg) { this.agreed = true; return false; }
      const v = this.read();
      const day = this.days.get(this.picked.day);
      const slot = day && day.slots.find((x) => x.id === v.slot);
      // ご予約内容（日時・人数・作品・備考）を、項目名と中身の表にする
      const list = dlg.querySelector('[data-confirm-list]');
      const rows = [];
      if (slot) rows.push(['slot', new Intl.DateTimeFormat(lang(), { month: 'long', day: 'numeric', weekday: 'short' }).format(day.date) + ' ' + slot.time]);
      if (v.guests) rows.push(['guests', /^\d+$/.test(v.guests) ? (this.dataset.guestsFormat || '{n}').replace('{n}', v.guestsLabel) : v.guestsLabel]);
      const titles = [...this.querySelectorAll('[data-field="interest"] input:checked')]
        .map((x) => x.closest('label')?.querySelector('.lounge-booking__tile-title')?.textContent.trim()).filter(Boolean);
      if (titles.length) rows.push(['interest', titles.join(lang().startsWith('ja') ? '、' : ', ')]);
      if (v.note) rows.push(['note', v.note]);
      list.replaceChildren(...rows.flatMap(([key, text]) => {
        const dt = document.createElement('dt'); dt.textContent = list.dataset['label' + key[0].toUpperCase() + key.slice(1)] || '';
        const dd = document.createElement('dd'); dd.textContent = text;
        return [dt, dd];
      }));
      const agree = dlg.querySelector('[data-confirm-agree]');
      dlg.querySelector('.lounge-booking__confirm-terms')?.removeAttribute('open');
      agree.checked = false;
      dlg.querySelectorAll('[data-confirm-go]').forEach((b) => b.setAttribute('aria-disabled', 'true'));
      dlg.querySelector('[data-confirm-error]').hidden = true;
      if (!this.confirmReady) {
        this.confirmReady = true;
        agree.addEventListener('change', () => {
          dlg.querySelectorAll('[data-confirm-go]').forEach((b) => b.setAttribute('aria-disabled', agree.checked ? 'false' : 'true'));
          if (agree.checked) dlg.querySelector('[data-confirm-error]').hidden = true;
        });
        dlg.querySelector('[data-confirm-close]').addEventListener('click', () => dlg.close());
        dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });   // 枠の外
        dlg.querySelectorAll('[data-confirm-go]').forEach((goBtn) => goBtn.addEventListener('click', async () => {
          if (!agree.checked) {   // 押せないボタンにはしない。押されたら理由を言う
            dlg.querySelector('[data-confirm-error]').hidden = false;
            // 同意の印は条件の全文の一番下。閉じていれば開いて、印のところまで送る（試着の窓と同じ）
            dlg.querySelector('.lounge-booking__confirm-terms')?.setAttribute('open', '');
            agree.scrollIntoView({ block: 'center', behavior: 'smooth' });
            agree.focus({ preventScroll: true });
            return;
          }
          this.agreed = true;
          this.sync();
          const form = this.form;
          if (!form) return;
          if (goBtn.hasAttribute('data-checkout')) {
            // 予約へ進む: カートに入れて、そのままチェックアウトへ
            goBtn.setAttribute('aria-busy', 'true');
            try {
              const root = window.Shopify?.routes?.root || '/';
              const res = await fetch(root + 'cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
              if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || res.status);
              window.location.href = root + 'checkout';
            } catch (e) {
              goBtn.removeAttribute('aria-busy');
              this.agreed = false; this.sync();
              const err = dlg.querySelector('[data-confirm-error]');
              err.textContent = String(e.message || e); err.hidden = false;
            }
            return;
          }
          dlg.close();
          form.requestSubmit(form.querySelector('button[name="add"]') || undefined);
        }));
      }
      if (!dlg.open) dlg.showModal();
      return true;
    }

    /* 送信を止めるかどうか。止めたら true */
    blockSubmit() {
      this.sync();
      if (this.isValid()) {
        if (!this.agreed) return this.openConfirm();
        // 同意して進んだ送信は通す。次に押したときはまた確認する
        setTimeout(() => { this.agreed = false; this.sync(); }, 0);
        return false;
      }
      this.showError(true);
      const first = this.querySelector('[data-invalid]');
      (first || this).scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
      const focusable = first && first.querySelector('button[data-day], input:not([disabled])');
      if (focusable) focusable.focus({ preventScroll: true });
      return true;
    }
  }

  customElements.define('lounge-booking-fields', LoungeBookingFields);

  // テーマの送信処理より先に受け取る（document の捕捉段階）
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    // form.id は使わない（注文フォームには name="id" の欄があり、そちらが返ってくる）
    const formId = form.getAttribute('id');
    if (!formId) return;
    const fields = document.querySelector(`lounge-booking-fields[data-form-id="${CSS.escape(formId)}"]`);
    if (fields && fields.blockSubmit()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
})();
